import { type ReactNode, useRef } from 'react';
import { cn } from '../../lib/utils';
import { GlassSlider, useGlassSlider } from './glass-slider';
import { GLASS_TAB_TRIGGER_CLASS, GLASS_TABS_LIST_CLASS } from './tabs';

export interface GlassSegmentedOption<T extends string> {
  readonly value: T;
  readonly label: ReactNode;
  readonly title?: string | undefined;
  readonly disabled?: boolean | undefined;
}

/**
 * Dải chọn **một trong vài** kiểu kính — cùng áo với `TabsList variant="glass"`,
 * nhưng cho thứ **không phải tab**: Danh sách/Lịch, Chưa xong/Tất cả, Tháng/
 * Quý/Năm, mức phóng Gantt… Mỗi lựa chọn đổi một tham số và giữ nguyên nội dung
 * bên dưới, nên nó là một cụm nút chứ không phải `tablist` với `tabpanel`.
 *
 * Mục đang chọn mang `aria-current="true"` — cũng là thứ viên trượt bám theo.
 * Không dùng `aria-pressed`: `.glass-hover[aria-pressed='true']` tự vẽ thấu
 * kính cho nút bật/tắt, và thêm viên trượt lên trên là hai lớp kính chồng nhau.
 *
 * `value = null` là **chưa chọn gì** (kết quả mục tiêu sprint chưa chấm, khoảng
 * ngày tự nhập không khớp preset nào): viên mờ đi, không lựa chọn nào đậm.
 *
 * ⚠️ Chỉ cho lựa chọn **loại trừ nhau**. Bật/tắt một mình (Quá hạn, Chỉ đường
 * găng) hay chọn nhiều (ngày trong tuần) vẫn là `Button` — một dải có viên trượt
 * nói "đúng một trong số này", và nói thế về một công tắc là nói sai.
 *
 * ⚠️ Đặt component này ở **đúng** chỗ nó hiện ra, đừng gắn hook ở cha: viên
 * trượt gắn vào phần tử lúc dựng, nên một dải có lúc ẩn mà hook nằm ở cha thì
 * lần dải hiện lại không có viên.
 */
export function GlassSegmented<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  size = 'default',
  disabled = false,
  className,
}: {
  readonly value: T | null;
  readonly options: readonly GlassSegmentedOption<T>[];
  readonly onChange: (value: T) => void;
  /** Nhãn cho trình đọc màn hình. Tuỳ chọn: vài cụm cũ vốn chưa có. */
  readonly ariaLabel?: string | undefined;
  /** `sm` cho hàng công cụ dày (Gantt, bộ lọc board): cao 32px, chữ nhỏ. */
  readonly size?: 'default' | 'sm';
  readonly disabled?: boolean;
  readonly className?: string | undefined;
}): ReactNode {
  const groupRef = useRef<HTMLDivElement | null>(null);
  const sliderRef = useGlassSlider(groupRef, '[aria-current="true"]');

  return (
    <div
      ref={groupRef}
      role="group"
      aria-label={ariaLabel}
      aria-disabled={disabled || undefined}
      className={cn(
        GLASS_TABS_LIST_CLASS,
        size === 'sm' && 'h-8',
        disabled && 'opacity-50',
        className,
      )}
    >
      <GlassSlider ref={sliderRef} />
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          aria-current={value === option.value}
          title={option.title}
          disabled={disabled || option.disabled}
          className={cn(GLASS_TAB_TRIGGER_CLASS, 'gap-1.5', size === 'sm' && 'px-3 text-xs')}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
